// src/components/warnings/warningCopy.js
//
// Pipeline warnings in OUR words (outputs ns → warnings.*), shared by the generator
// run card (Step3History) and the run detail page. Contract:
// <BACKEND_FOLDER>/docs/frontend/api-contract.md § Warnings and endpoints.md
// «Pipeline warning codes».
// ⛔ Never render `message` or `details.error` (English server prose / exception
//   names): an unknown code prints the CODE (a support handle).
// Severity → tone: critical/fatal red (never muted), warning/recoverable amber,
// info neutral, missing/unknown amber.
import { dedupeItems } from '../claims/claimsModel.js';

const TONE_BY_SEVERITY = { critical: 'errore', fatal: 'errore', warning: 'attenzione', recoverable: 'attenzione', info: 'neutro' };
const TONE_RANK = { errore: 0, attenzione: 1, neutro: 1 };

// Codes worded by title + message only (no payload shown).
const SIMPLE_CODES = new Set([
  'VERIFICATION_UNAVAILABLE', 'REFERENCES_UNAVAILABLE', 'UNREVIEWED_ERROR', 'FACT_CHECK_SKIPPED', 'MAX_ITERATIONS_REACHED',
  'SITEMAP_FETCH_FAILED', 'DEPRECATED_KEYWORDS_SCHEMA', 'BUNDLE_QUERY_FAILED', 'EVIDENCE_GATHERING_FAILED',
  'RESEARCHER_OUTPUT_PARSE_FAILED', 'KEYWORDS_NOT_CONFIGURED', 'KEYWORD_PRIMARY_MISSING_H2',
  'KEYWORD_STUFFING', 'AI_TELL_EMOJI_DETECTED',
]);

export const severityTone = (severity) => TONE_BY_SEVERITY[severity] || 'attenzione';

// Red first; stable inside a tone (Array.prototype.sort is stable); never mutates.
export const sortBySeverity = (warnings) =>
  [...(warnings || [])].sort(
    (a, b) => TONE_RANK[severityTone(a?.severity)] - TONE_RANK[severityTone(b?.severity)],
  );

// Map raw agent identifiers (e.g. "critic", "critic_node.web_search",
// "researcher_node.url_fetch") to a friendly phase label (i18n via outputs ns).
export const friendlyAgentLabel = (agent, tr) => {
  if (!agent) return tr('agentLabels.system');
  if (agent.startsWith('critic')) return tr('agentLabels.factCheck');
  if (agent.startsWith('researcher')) return tr('agentLabels.research');
  if (agent.startsWith('writer')) return tr('agentLabels.writing');
  if (agent.startsWith('curator')) return tr('agentLabels.curation');
  if (agent.startsWith('verification')) return tr('agentLabels.verification');
  if (agent.startsWith('style')) return tr('agentLabels.style');
  return tr('agentLabels.system'); // ⛔ never the raw agent id
};

// Collapse multiple LANDING_REQUIRED_BLOCK_EMPTY warnings (one per missing
// field) into a single synthetic banner so the user sees ONE amber box
// listing all empty fields instead of N separate boxes. Non-landing
// warnings pass through unchanged. Preserves order: synthetic entry takes
// the position of the FIRST consolidated warning, rest stays in place.
export const consolidateLandingWarnings = (warnings) => {
  if (!Array.isArray(warnings) || warnings.length === 0) return warnings;
  let firstReqIdx = -1;
  const fields = [];
  const rest = [];
  warnings.forEach((w, i) => {
    if (w?.code === 'LANDING_REQUIRED_BLOCK_EMPTY') {
      if (firstReqIdx === -1) firstReqIdx = i;
      const block = w.details?.block;
      const field = w.details?.field;
      if (block && field) fields.push(`${block}.${field}`);
    } else {
      rest.push(w);
    }
  });
  if (fields.length === 0) return warnings;
  const synthetic = {
    code: 'LANDING_REQUIRED_BLOCK_EMPTY',
    agent: 'curator.landing',
    severity: 'warning',
    message: '',
    details: { fields },
  };
  // Insert synthetic at the position of the first consolidated warning.
  const out = [...rest];
  const insertAt = Math.min(firstReqIdx, out.length);
  out.splice(insertAt, 0, synthetic);
  return out;
};

// Critic-aware wording shared by WEB_SEARCH_FAILED and LLM_SERVICE_UNAVAILABLE.
const criticVariant = (code, isCritic, tr) => (isCritic
  ? { title: tr(`warnings.${code}.criticTitle`), message: tr(`warnings.${code}.criticMessage`) }
  : { title: tr(`warnings.${code}.title`), message: tr(`warnings.${code}.message`) });

const requestUrlCopy = (warning, tr) => {
  const failedUrls = warning.details?.failed_urls || [];
  const totalRequested = warning.details?.total_requested ?? failedUrls.length;
  const totalSucceeded = warning.details?.total_succeeded ?? 0;
  const failed = failedUrls.length || (totalRequested - totalSucceeded);
  return {
    title: tr('warnings.REQUEST_URL_FETCH_FAILED.title'),
    message: tr('warnings.REQUEST_URL_FETCH_FAILED.message', { failed, totalRequested }),
    urls: failedUrls,
  };
};

const landingEmptyCopy = (warning, tr) => {
  const fields = warning.details?.fields || [];
  if (fields.length === 0) {
    return {
      title: tr('warnings.LANDING_REQUIRED_BLOCK_EMPTY.title'),
      message: tr('warnings.LANDING_REQUIRED_BLOCK_EMPTY.messageEmpty'),
    };
  }
  const countPhrase = tr('warnings.LANDING_REQUIRED_BLOCK_EMPTY.fieldCount', { count: fields.length });
  return {
    title: tr('warnings.LANDING_REQUIRED_BLOCK_EMPTY.title'),
    message: tr('warnings.LANDING_REQUIRED_BLOCK_EMPTY.message', { countPhrase, fields: fields.join(', ') }),
  };
};

const socialProofDroppedCopy = (warning, tr) => {
  const op = String(warning.details?.operator_input || '').slice(0, 200).trim();
  return {
    title: tr('warnings.LANDING_OPERATOR_SOCIAL_PROOF_DROPPED.title'),
    message: op
      ? tr('warnings.LANDING_OPERATOR_SOCIAL_PROOF_DROPPED.messageWithOp', { op })
      : tr('warnings.LANDING_OPERATOR_SOCIAL_PROOF_DROPPED.message'),
  };
};

const citedSourceCopy = (warning, tr) => {
  const items = Array.isArray(warning.details?.items) ? warning.details.items : [];
  return {
    title: tr('warnings.CITED_SOURCE_NOT_ACCESSIBLE.title'),
    message: tr('warnings.CITED_SOURCE_NOT_ACCESSIBLE.message'),
    refs: items
      .filter((i) => i && Number.isInteger(i.marker))
      .map((i) => tr('warnings.itemRef', { marker: i.marker, title: i.title || '' }).trim()),
  };
};

// Translate a backend warning into non-technical copy keyed by `code`.
// Returns `{ title, message, urls?, items?, refs? }`.
export const friendlyWarningCopy = (warning, tr) => {
  const code = warning.code;
  const isCritic = (warning.agent || '').startsWith('critic');

  if (code === 'REQUEST_URL_FETCH_FAILED') return requestUrlCopy(warning, tr);
  if (code === 'WEB_SEARCH_FAILED' || code === 'LLM_SERVICE_UNAVAILABLE') return criticVariant(code, isCritic, tr);
  if (code === 'LANDING_REQUIRED_BLOCK_EMPTY') return landingEmptyCopy(warning, tr);
  if (code === 'LANDING_OPERATOR_SOCIAL_PROOF_DROPPED') return socialProofDroppedCopy(warning, tr);

  if (code === 'BRAND_TABOO_IN_TEXT' || code === 'TERMINOLOGY_MISMATCH') {
    return {
      title: tr(`warnings.${code}.title`),
      message: tr(`warnings.${code}.message`),
      items: dedupeItems(warning.details?.items),
    };
  }

  if (code === 'CITED_SOURCE_NOT_ACCESSIBLE') return citedSourceCopy(warning, tr);

  if (SIMPLE_CODES.has(code)) {
    return { title: tr(`warnings.${code}.title`), message: tr(`warnings.${code}.message`) };
  }

  // Fallback: our sentence; the code as a support handle. ⛔ never warning.message.
  return {
    title: tr('warnings.fallback.title', { agent: friendlyAgentLabel(warning.agent || '', tr) }),
    message: code
      ? tr('warnings.fallback.messageWithCode', { code })
      : tr('warnings.fallback.message'),
  };
};
